import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import EcfBadge, { type BadgeVariant } from './EcfBadge';
import { DOC_TYPE_LABEL } from '../catalogos';
import {
  ecfService,
  type CertCaseResult,
  type CertPreview,
  type EcfConfig,
} from '../../../../services/ecf.service';

/**
 * Panel del Paso 2 de la certificación: el set de pruebas de la DGII.
 *
 * El flujo tiene dos tiempos a propósito. Primero se firma sin enviar, para comprobar que los 25
 * comprobantes se arman bien; solo después se envía. Es que enviar es irreversible: la DGII
 * consume el e-NCF al primer intento y no admite reenviarlo, ni siquiera si lo rechazó.
 */

const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  pendiente: { label: 'Pendiente', variant: 'muted' },
  listo: { label: 'Firmado', variant: 'info' },
  enproceso: { label: 'En proceso', variant: 'warning' },
  aceptado: { label: 'Aceptado', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'danger' },
  error: { label: 'Error', variant: 'danger' },
};

interface PruebasDatosPanelProps {
  config: EcfConfig;
}

export default function PruebasDatosPanel({ config }: PruebasDatosPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const [xsd, setXsd] = useState<{ tipos: string[]; total: number } | null>(null);
  const [preview, setPreview] = useState<CertPreview | null>(null);
  const [results, setResults] = useState<CertCaseResult[]>([]);

  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [x, r] = await Promise.all([ecfService.certXsdStatus(), ecfService.certResults()]);
      setXsd(x);
      setResults(r);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'No se pudo cargar el estado',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ambienteOk = config.environment === 'cert';
  const listoParaEnviar = ambienteOk && !!config.emisor_rnc && !!config.emisor_name;

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setMessage(null);
    try {
      const p = await ecfService.certPreview(file);
      setPreview(p);
      const conError = p.cases.filter((c) => c.status === 'error').length;
      if (conError) {
        setMessage({
          type: 'error',
          text: `${conError} de ${p.total} comprobantes no se pudieron armar. Revisa el detalle antes de enviar.`,
        });
      } else {
        setMessage({
          type: 'success',
          text: `${p.total} comprobantes armados y firmados correctamente: ${p.ecfCount} íntegros y ${p.rfceCount} resúmenes de consumo. Nada se ha enviado todavía.`,
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'No se pudo procesar el set',
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleRun = async () => {
    if (!file || !preview) return;

    const porEnviar = preview.total - preview.yaEnviados;
    const aviso =
      `Se enviarán ${porEnviar} comprobantes a la DGII (ambiente ${preview.environment}).\n\n` +
      `Esto NO se puede deshacer: cada e-NCF queda consumido en la DGII y no se puede reenviar, ` +
      `ni siquiera si lo rechaza.\n\n¿Continuar?`;
    if (!confirm(aviso)) return;

    setSending(true);
    setMessage(null);
    try {
      const r = await ecfService.certRun(file);
      await load();
      const partes = [`${r.enviados} enviados`];
      if (r.saltados) partes.push(`${r.saltados} ya estaban enviados`);
      if (r.fallidos) partes.push(`${r.fallidos} con error`);
      setMessage({
        type: r.fallidos ? 'error' : 'success',
        text: partes.join(' · '),
      });
      toast[r.fallidos ? 'warning' : 'success'](`Envío terminado: ${partes.join(', ')}`);
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Falló el envío';
      setMessage({ type: 'error', text });
      toast.error(text);
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setResults(await ecfService.certRefreshStatus());
      toast.success('Estados actualizados');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudieron actualizar los estados');
    } finally {
      setRefreshing(false);
    }
  };

  const handleReset = async () => {
    if (
      !confirm(
        'Esto borra el registro local de lo enviado, pero NO lo deshace en la DGII.\n\n' +
          'Si vuelves a enviar, esos e-NCF fallarán por estar consumidos. Úsalo solo si la DGII reinició tu postulación.\n\n¿Continuar?'
      )
    )
      return;
    try {
      await ecfService.certReset();
      setPreview(null);
      await load();
      toast.success('Registro local reiniciado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo reiniciar');
    }
  };

  const descargar = async (encf: string) => {
    try {
      await ecfService.certDownloadXml(encf);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo descargar el XML');
    }
  };

  // Los resultados mandan sobre la vista previa: reflejan lo que la DGII respondió de verdad.
  const filas: CertCaseResult[] = results.length ? results : preview?.cases ?? [];
  const aceptados = results.filter((r) => r.status === 'aceptado');
  const rfceAceptados = aceptados.filter((r) => r.send_via === 'rfce').length;
  const ecfAceptados = aceptados.filter((r) => r.send_via === 'ecf').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Requisitos */}
      {!listoParaEnviar && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm space-y-1">
          <p className="font-medium">
            <i className="ri-error-warning-line mr-1"></i>
            Falta configuración antes de poder enviar
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-xs">
            {!ambienteOk && (
              <li>
                El ambiente activo es <strong>{config.environment}</strong>. El set de
                certificación solo cuenta en CerteCF.
              </li>
            )}
            {!config.emisor_rnc && <li>Falta el RNC del emisor.</li>}
            {!config.emisor_name && (
              <li>
                Falta la razón social. Debe coincidir exactamente con la registrada para el RNC.
              </li>
            )}
          </ul>
        </div>
      )}

      {message && (
        <div
          className={`p-4 rounded-lg border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          <i
            className={`${
              message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            } mr-2`}
          ></i>
          {message.text}
        </div>
      )}

      {/* 1. Cargar el set */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">1. Carga el set de pruebas</p>
        <p className="text-xs text-gray-500">
          Descárgalo del portal de la DGII con "Descargar comprobantes". Cada fila se convierte en
          un comprobante que se firma con tu certificado.
          {xsd && (
            <>
              {' '}
              Esquemas cargados: <strong>{xsd.total}/10</strong>.
            </>
          )}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
          }}
          className="text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50 file:text-sm file:text-gray-700 file:cursor-pointer hover:file:bg-gray-100"
        />
      </div>

      {/* 2. Firmar sin enviar */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">2. Comprueba antes de enviar</p>
        <p className="text-xs text-gray-500">
          Arma y firma los comprobantes sin enviarlos. Si alguno falla, se corrige aquí sin gastar
          ningún e-NCF.
        </p>
        <button
          onClick={handlePreview}
          disabled={!file || previewing}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i
            className={`${previewing ? 'ri-loader-4-line animate-spin' : 'ri-quill-pen-line'} mr-2`}
          ></i>
          {previewing ? 'Firmando...' : 'Firmar sin enviar'}
        </button>

        {preview && (
          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-0.5">
            <p>
              Emisor: <strong>{preview.razonSocial}</strong> · RNC {preview.emisorRnc} · ambiente{' '}
              {preview.environment}
            </p>
            <p>
              {preview.total} comprobantes: {preview.ecfCount} íntegros y {preview.rfceCount}{' '}
              resúmenes de consumo
              {preview.yaEnviados > 0 && ` · ${preview.yaEnviados} ya enviados (se saltarán)`}
            </p>
          </div>
        )}
      </div>

      {/* 3. Enviar */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">3. Envía a la DGII</p>
        <p className="text-xs text-gray-500">
          Irreversible: cada e-NCF queda consumido y no se puede reenviar, ni siquiera si la DGII
          lo rechaza. Los ya enviados se saltan automáticamente.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleRun}
            disabled={!file || !preview || sending || !listoParaEnviar}
            title={!preview ? 'Primero firma sin enviar' : undefined}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i
              className={`${sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} mr-2`}
            ></i>
            {sending ? 'Enviando a la DGII...' : 'Enviar a la DGII'}
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing || !results.some((r) => r.status === 'enproceso')}
            title="La DGII valida en diferido; esto vuelve a consultar los que quedaron en proceso"
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i
              className={`${refreshing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} mr-2`}
            ></i>
            Actualizar estados
          </button>

          {results.length > 0 && (
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-red-600 text-sm font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              Reiniciar registro
            </button>
          )}
        </div>
      </div>

      {/* Aviso del flujo de consumo < 250 mil */}
      {rfceAceptados > 0 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-3 text-sm">
          <p className="font-medium mb-1">
            <i className="ri-information-line mr-1"></i>
            Falta un paso manual en el portal
          </p>
          <p className="text-xs">
            {rfceAceptados} resúmenes de consumo fueron aceptados. Ahora hay que subir esas
            facturas <strong>íntegras</strong> en el formulario "Facturas de consumo &lt; 250Mil"
            del portal. Descarga su XML con el botón de cada fila: es el mismo archivo firmado que
            generó el resumen aceptado, y su código de seguridad tiene que coincidir.
          </p>
        </div>
      )}

      {/* Resultados */}
      {filas.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium text-gray-900">
              Comprobantes
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({filas.length} en total)
              </span>
            </p>
            {results.length > 0 && (
              <p className="text-xs text-gray-600">
                Aceptados: <strong>{ecfAceptados}</strong> íntegros ·{' '}
                <strong>{rfceAceptados}</strong> resúmenes
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    e-NCF
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vía
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código seg.
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    XML
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filas.map((r) => {
                  const badge = STATUS_BADGE[r.status] ?? STATUS_BADGE.pendiente;
                  return (
                    <tr key={r.encf} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono font-semibold text-gray-900">
                        {r.encf}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {r.doc_type}
                        <span className="block text-xs text-gray-500">
                          {DOC_TYPE_LABEL[r.doc_type]}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                        {r.send_via === 'rfce' ? 'Resumen' : 'Íntegro'}
                      </td>
                      <td className="px-4 py-2">
                        <EcfBadge variant={badge.variant}>{badge.label}</EcfBadge>
                        {r.message && (
                          <span className="block text-xs text-gray-500 mt-1 max-w-md">
                            {r.message}
                          </span>
                        )}
                        {r.track_id && (
                          <span className="block text-[11px] font-mono text-gray-400 mt-1">
                            {r.track_id}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-gray-700">
                        {r.security_code ?? '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {r.has_xml ? (
                          <button
                            onClick={() => descargar(r.encf)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Descargar XML firmado"
                          >
                            <i className="ri-download-line"></i>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
